import React, { useEffect, useRef, useState } from "react";
import {
  Route,
  Switch,
  Redirect,
  useHistory,
  useLocation
} from "react-router-dom";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import qs from "query-string";
import { ErrorBoundary } from "react-error-boundary";

import {
  getBaseUrl,
  getCustomerId,
  getEComSessionId,
  getVisitorId,
  setCustomerId
} from "@/util";
import {
  setPrevModeStorage,
  removePrevModeStorage,
  getUserStorage
} from "@/util/browserStorage";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { setOTPLoginFlowBFL } from "@/redux/actions/homepage.action";
import {
  fetchUserAndLoadData,
  resetAuthData,
  setToken,
  showPrevMode
} from "@/redux/actions/authConfig.action";
import {
  selectLoggedInStatus,
  selectLoyaltyJoinData,
  selectOTPLoginFlowBFL
} from "@/util/selectors";
import AuthAnaltyicsEvent from "@/services/analytics/main/authentication";
import { useLanguage, useRouteSlug } from "@/hooks";

import Header from "@/components/header";
import Footer from "@/components/footer/footer";
import ScrollToTop from "@/components/scroll-to-top";
import PreviewMode from "@/components/preview-mode";
import AppContainer from "./appContainer";
import View from "@/components/common/view";
import MobileHeader from "@/components/header/mobile-header";
import ErrorFallback from "@/components/errorFallback";
import LoyaltyJoinModal from "@/components/loyalty/joinLoyaltyModal";
import { routes } from "./routes";
import { DynamicYieldEventService } from "@/services";
import CommonAnalyticsEvent from "@/services/analytics/main/common";
import { clearAuthEmail } from "@/util/auth";
import { normalizePathFromQuery } from "@/utils";

const pagesWithoutHeader = [
  "checkout",
  "payment-error",
  "payment-processing",
  "unsubscribe",
  "retry-payment",
  "protected-captcha"
];
const pagesWithoutJoinLoyaltyModal = [
  "checkout",
  "login",
  "sign-up",
  "guest-sign-up",
  "authorization",
  "update-details",
  "unsubscribe",
  "retry-payment"
];
const hideInMobileValues = [
  "p",
  "my-cart",
  "wishlist",
  "authorization",
  "login",
  "sign-up",
  "update-details"
];
const entryPageRoute = "/";

const authDataPages = new Set([
  "login",
  "sign-up",
  "authorization",
  "forgot-password",
  "update-details"
]);

const redirectKeys = ["redirect"];

const AuthContent = () => {
  const [footerSpace, setFooterSpace] = useState(false);
  const [hideInMobile, setHideInMobile] = useState(false);
  const [showLoyaltyJoinProgram, setShowLoyaltyJoinProgram] = useState(false);
  const language = useLanguage();
  const userLoggedIn = useAppSelector(state => state.authReducer.userLoggedIn);
  const settings = useAppSelector(state => state.common.settings);
  const isPreviewMode = useAppSelector(state => state.authReducer.showPreview);
  const isLoggedIn = useAppSelector(selectLoggedInStatus);
  const { loyaltyPopupDelayTime, loyaltyPopupViewsPerDay } = useAppSelector(
    selectLoyaltyJoinData
  );
  const routeSlug = useRouteSlug();
  const otpLoginFlowBFL = useAppSelector(state => selectOTPLoginFlowBFL(state));

  const history = useHistory();
  const location = useLocation();
  const { pathname, search } = location;
  const dispatch = useAppDispatch();
  const accessTokenFromCookies = getUserStorage();
  const isAuthPath = pathname.split("/").some(seg => authDataPages.has(seg));
  const prevIsAuthRef = useRef<boolean>(isAuthPath);

  const getHideHeaderValue = () =>
    pathname === entryPageRoute ||
    pagesWithoutHeader.some(page => pathname.includes(page));

  const [hideHeader, setHideHeader] = useState(getHideHeaderValue());

  useEffect(() => {
    const { prevMode } = qs.parse(search);

    if (prevMode) {
      setPrevModeStorage(prevMode);

      const newUrl = qs.stringifyUrl({
        url: pathname,
        query: {}
      });

      history.push(newUrl);
    }
  }, []);

  useEffect(() => {
    const wasAuth = prevIsAuthRef.current;
    if (wasAuth && !isAuthPath) {
      dispatch(resetAuthData());
      clearAuthEmail();
    }

    prevIsAuthRef.current = isAuthPath;
  }, [isAuthPath, dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      const customerIdFromCookies = getCustomerId();
      if (settings.countryId) {
        getEComSessionId();
      }
      if (settings.countryId && userLoggedIn) {
        const response = await dispatch(
          fetchUserAndLoadData({ keepDeliverySettings: true })
        );
        const accessTokenFromResponse = get(
          response,
          "data.userProfile.accessToken"
        );
        if (
          isLoggedIn &&
          !isEmpty(accessTokenFromResponse) &&
          !isEmpty(accessTokenFromCookies) &&
          accessTokenFromResponse !== accessTokenFromCookies
        ) {
          dispatch(setToken({ accessToken: accessTokenFromResponse }));
        }
        const isOTPLoginFlowBFL =
          otpLoginFlowBFL && response.status === 200 && response.data.success;

        if (isOTPLoginFlowBFL) {
          dispatch(setOTPLoginFlowBFL(false));
          AuthAnaltyicsEvent.emailSignIn({
            loginFrom: "email",
            responseData: response.data
          });

          const email = get(response, "data.userProfile.email", "");
          if (email) {
            DynamicYieldEventService.login(email);
          }
        }
        const customerId = get(response, "data.userProfile.customerId", "");
        CommonAnalyticsEvent.trackSessionStart(customerId);
        if (!customerIdFromCookies) {
          setCustomerId(customerId);
        }
      }
      if (settings?.countryId) {
        CommonAnalyticsEvent.changeLanguage({
          code: language
        });
      }
    };

    fetchData();
  }, [settings.countryId, userLoggedIn]);

  useEffect(() => {
    const pathSegments = pathname.split("/");
    const isHideHeader = getHideHeaderValue();
    const isChangeFooterSpace = pathname.includes("product-detail");
    const isHideInMobile = pathSegments.some(segment =>
      hideInMobileValues.includes(segment)
    );

    if (isHideHeader !== hideHeader) {
      setHideHeader(isHideHeader);
    }

    if (isChangeFooterSpace !== footerSpace) {
      setFooterSpace(isChangeFooterSpace);
    }

    if (isHideInMobile !== hideInMobile) {
      setHideInMobile(isHideInMobile);
    }
  }, [pathname]);

  useEffect(() => {
    const showLoyaltyJoinModal =
      !isLoggedIn &&
      !pagesWithoutJoinLoyaltyModal.some(page => pathname.includes(page)) &&
      Boolean(+loyaltyPopupDelayTime && +loyaltyPopupViewsPerDay);

    setShowLoyaltyJoinProgram(showLoyaltyJoinModal);
  }, [pathname, loyaltyPopupDelayTime, loyaltyPopupViewsPerDay, isLoggedIn]);

  const exitPrevMode = () => {
    dispatch(showPrevMode(false));
    removePrevModeStorage(document.domain);
    history.push(getBaseUrl({ language, settings }) + "/");
  };

  let redirectPaths = [];
  if (userLoggedIn) {
    redirectPaths = ["/login", "/sign-up", "/authorization"];
  }

  const isAuthRedirectGate =
    userLoggedIn && redirectPaths.some(p => pathname.includes(p));

  const getRedirectTargetFromSearch = (search: string) => {
    if (!search || search === "?") return "";

    const raw = search.startsWith("?") ? search.slice(1) : search;
    const decoded = decodeURIComponent(raw);

    const looksLikeBarePath =
      !decoded.includes("=") && !decoded.includes("&") && decoded.length > 0;

    if (looksLikeBarePath) {
      return normalizePathFromQuery(decoded);
    }

    const parsed = qs.parse(search);

    for (const key of redirectKeys) {
      const val = (parsed as any)[key];
      const value = Array.isArray(val) ? val[0] : val;
      if (typeof value === "string" && value.trim()) {
        return normalizePathFromQuery(value);
      }
    }
    return "";
  };

  const redirectTargetFromAuthQuery = isAuthRedirectGate
    ? getRedirectTargetFromSearch(search)
    : "";

  const switchRoutes = routes.map(route => {
    return (
      <Route
        exact={!!route.exact}
        path={route.path}
        component={route.component}
        key={route.path}
      >
        {redirectPaths.some(path => route.path.includes(path)) && (
          <Redirect to={`/${routeSlug}/`} />
        )}
      </Route>
    );
  });

  return (
    <ScrollToTop>
      {isPreviewMode && <PreviewMode exitPrevMode={exitPrevMode} />}
      {showLoyaltyJoinProgram ? (
        <LoyaltyJoinModal
          repeats={loyaltyPopupViewsPerDay}
          loyaltyPopupTimer={loyaltyPopupDelayTime}
        />
      ) : null}
      {!hideHeader && (
        <>
          <View.desktop>
            <Header />
          </View.desktop>

          <View.mobile>
            <MobileHeader showOnlyBanner={!hideInMobile} />
          </View.mobile>
        </>
      )}

      <AppContainer>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Switch>
            {isAuthRedirectGate && redirectTargetFromAuthQuery ? (
              <Redirect to={`/${routeSlug}/${redirectTargetFromAuthQuery}`} />
            ) : null}

            {switchRoutes}
          </Switch>
        </ErrorBoundary>
      </AppContainer>
      {!hideHeader && (
        <Footer hideInMobile={hideInMobile} footerSpace={footerSpace} />
      )}
    </ScrollToTop>
  );
};

export default AuthContent;
